import type { WebhookPartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Link from '@/components/link';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import WebhookDeliveryStatusBadge from './WebhookDeliveryStatusBadge';
import WebhookStatusBadge from './WebhookStatusBadge';

export type WebhookTableFields = WebhookPartsFragment & {
  CreatedByUserName: null | string;
  EventTypeCount: number;
  [key: string]: unknown;
};

export const useGetCollectionTableProps = (
  records: WebhookPartsFragment[],
  onEdit?: (webhook: WebhookPartsFragment) => void
): TablePropsWithActions<WebhookTableFields> => {
  const { t } = useTranslation(['common'], { keyPrefix: 'webhooks' });
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'webhooks.columns',
  });

  const labelledFields = useMemo<WebhookTableFields[]>(
    () =>
      records.map((record) => ({
        ...record,
        CreatedByUserName: record.createdByUser?.FriendlyName ?? null,
        EventTypeCount: record.subscriptions.length,
      })),
    [records]
  );

  const fields: TableFields<WebhookTableFields> = {
    Title: {
      header: st('name'),
      cell: (item) =>
        onEdit ? (
          <Link variant={'secondary'} href={'#'} onFollow={() => onEdit(item)}>
            {item.Title}
          </Link>
        ) : (
          item.Title
        ),
    },
    WebhookUrl: {
      header: st('url'),
    },
    Status: {
      header: st('status'),
      cell: (c) => <WebhookStatusBadge status={c.Status} />,
    },
    LastDeliveryStatus: {
      header: st('lastDelivery'),
      // No delivery rows yet → fall back to the `pending` sentinel.
      cell: (c) => (
        <WebhookDeliveryStatusBadge
          status={c.deliveries[0]?.Status ?? 'pending'}
        />
      ),
      // Derived from the last delivery log; filtering it isn't meaningful.
      excludeFromFilter: true,
    },
    EventTypeCount: {
      header: st('eventTypes'),
      fieldType: 'number',
    },
    CreatedByUserName: {
      header: st('createdByUser'),
    },
    CreatedAtTimestamp: dateColumnFromConfig({
      header: { header: st('createdAtTimestamp') },
      dateField: 'CreatedAtTimestamp',
    }),
  };

  return useGetTableProps({
    tableId: 'organisationWebhooksRegister',
    data: labelledFields,
    entityLabel: t('entity_name'),
    fields,
    initialColumns: [
      'Title',
      'WebhookUrl',
      'Status',
      'LastDeliveryStatus',
      'EventTypeCount',
      'CreatedByUserName',
      'CreatedAtTimestamp',
    ],
    preferencesStorageKey: 'OrganisationWebhooksSettingsTable-Preferences',
    customAttributeFormIds: [],
  });
};
